import { useMemo, useState } from "react";
import { ContactRow } from "../../api/relationship.type";
import { getContactDisplayName } from "../../utils/contactNotifications";
import Avatar from "../shared/avatar";
import HarperLoadingSpinner from "../shared/HarperLoadingSpinner";
import { Modal } from "../shared/modal";
import Typography from "../shared/typography";
import Input from "../shared/input";

interface ContactRecordsPickerModalProps {
  contacts: ContactRow[];
  isLoading: boolean;
  isOpen: boolean;
  onClose: () => void;
  onSelect: (contact: ContactRow) => void;
}

const formatRole = (role?: string | null) => {
  if (!role) return "Contact";
  return role.charAt(0).toUpperCase() + role.slice(1);
};

export function ContactRecordsPickerModal({
  contacts,
  isLoading,
  isOpen,
  onClose,
  onSelect,
}: ContactRecordsPickerModalProps) {
  const [search, setSearch] = useState("");

  const sortedContacts = useMemo(() => {
    return [...contacts].sort((a, b) => {
      const nameA = getContactDisplayName(a).toLowerCase();
      const nameB = getContactDisplayName(b).toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [contacts]);

  const filteredContacts = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return sortedContacts;

    return sortedContacts.filter((contact) => {
      const first = contact.related_user?.first_name?.toLowerCase() ?? "";
      const last = contact.related_user?.last_name?.toLowerCase() ?? "";
      const fallback = getContactDisplayName(contact).toLowerCase();
      return first.includes(term) || last.includes(term) || fallback.includes(term);
    });
  }, [search, sortedContacts]);

  const hasContacts = sortedContacts.length > 0;
  const hasVisibleContacts = filteredContacts.length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="View a contact's medical records"
      footer={
        <div className="flex justify-end">
          <button
            type="button"
            className="rounded-md border border-brown-200 px-4 py-2 text-sm font-medium text-brown-700 transition-colors hover:bg-cream-100"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <Typography variant="body2" className="text-brown-700">
          Select a contact to jump directly to the records they share with you.
        </Typography>

        {hasContacts && (
          <Input
            id="contact-search"
            label="Search contacts"
            placeholder="Search by first or last name"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            fullWidth
          />
        )}

        {isLoading ? (
          <HarperLoadingSpinner
            label="Loading contacts"
            description="Checking who has shared their records with you."
          />
        ) : hasContacts ? (
          <div className="max-h-[60vh] overflow-y-auto rounded-lg border border-beige-200 p-2">
            {hasVisibleContacts ? (
              <ul className="space-y-2">
                {filteredContacts.map((contact) => {
                  const name = getContactDisplayName(contact);
                  const photoUrl =
                    contact.photo_url || contact.related_user?.avatar_url;

                  return (
                    <li key={contact.id}>
                      <button
                        type="button"
                        className="flex w-full items-center gap-3 rounded-md border border-beige-200 bg-white px-4 py-3 text-left transition-colors hover:bg-cream-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-terracotta-500"
                        onClick={() => onSelect(contact)}
                      >
                        <Avatar
                          src={photoUrl ?? undefined}
                          fallbackText={name}
                          size="sm"
                          className="flex-shrink-0"
                        />
                        <div className="flex flex-col">
                          <Typography
                            variant="subtitle2"
                            className="text-brown-900"
                          >
                            {name}
                          </Typography>
                          <Typography
                            variant="caption"
                            className="text-brown-600"
                          >
                            {formatRole(contact.related_user_role)}
                          </Typography>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="flex h-full flex-col items-center justify-center rounded-lg bg-cream-50 p-4 text-center">
                <Typography variant="subtitle2" className="text-brown-700">
                  No contacts match "{search}"
                </Typography>
                <Typography variant="body2" className="mt-1 text-brown-600">
                  Try searching by first or last name.
                </Typography>
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-lg border border-beige-300 bg-cream-50 p-4 text-center">
            <Typography variant="subtitle2" className="text-brown-700">
              No contacts with record access yet
            </Typography>
            <Typography variant="body2" className="mt-1 text-brown-600">
              Ask your contacts to share their medical records from the Contacts
              page so you can manage them here.
            </Typography>
          </div>
        )}
      </div>
    </Modal>
  );
}
